import axios from "axios";
import Cookies from "js-cookie";
import { BASE_URL } from "../api";
import MessageToster from "../component/MessageToster";
import { getApiHeaders } from "./helpers";

/**
 * Call backend API /admin/create_token to generate a login token for a given mid
 * @param {string|number} mid - Member ID
 * @returns {Promise<{success: boolean, token?: string, message?: string, data?: any}>}
 */
export const createAdminToken = async (mid) => {
    if (!mid && mid !== 0) {
        return {
            success: false,
            message: "Member ID (mid) is required.",
        };
    }

    try {
        // Backend expects mid as string
        const payload = { mid: String(mid).trim() };

        const res = await axios.post(`${BASE_URL}/admin/create_token`, payload, {
            headers: getApiHeaders(),
        });

        if (res.data && (res.data.code === 200 || res.data.status === "success" || res.data.access_token)) {
            const token = res.data.access_token || res.data.token || res.data.data?.access_token || res.data.data?.token;

            if (!token) {
                return {
                    success: false,
                    message: res.data.message || "Token not found in response.",
                    data: res.data,
                };
            }

            return {
                success: true,
                token,
                message: res.data.message || "Token created successfully.",
                data: res.data,
            };
        } else {
            return {
                success: false,
                message: res.data?.message || "Failed to create token.",
                data: res.data,
            };
        }
    } catch (err) {
        const errorMsg =
            err.response?.data?.detail?.[0]?.msg ||
            err.response?.data?.detail ||
            err.response?.data?.message ||
            err.message ||
            "Failed to create admin login token.";

        return {
            success: false,
            message: errorMsg,
            error: err,
        };
    }
};

/**
 * Perform direct login as user by mid:
 * 1. Generates token via createAdminToken(mid)
 * 2. Sets token in Cookies & mid in sessionStorage
 * 3. Cleans previous session storage/cookies
 * 4. Redirects to dashboard or specified route
 * 
 * @param {string|number} mid - Member ID
 * @param {object} options
 * @param {string} [options.redirectUrl="/dashboard"] - URL to redirect after successful login
 * @param {boolean} [options.autoRedirect=true] - Whether to automatically redirect
 * @param {boolean} [options.showToast=true] - Whether to show success toast
 * @returns {Promise<{success: boolean, token?: string, message?: string}>}
 */
export const adminDirectLogin = async (mid, options = {}) => {
    const {
        redirectUrl = "/dashboard",
        autoRedirect = true,
        showToast = true,
    } = options;

    if (!mid && mid !== 0) {
        if (showToast) {
            MessageToster("error", "Member ID (mid) is required.");
        }
        return { success: false, message: "Member ID (mid) is required." };
    }

    const trimmedMid = String(mid).trim();
    const tokenResult = await createAdminToken(trimmedMid);

    if (!tokenResult.success || !tokenResult.token) {
        if (showToast) {
            MessageToster("error", tokenResult.message || "Admin login failed.");
        }
        return tokenResult;
    }

    try {
        // Clear any previous logout flags
        sessionStorage.removeItem("manual_logout");

        // Clear previous user cache
        Cookies.remove("userdata");
        Cookies.remove("username");

        // Set auth token in cookies
        Cookies.set("token", tokenResult.token, {
            secure: false,
            sameSite: "lax",
            expires: 7,
        });

        // Set member ID in sessionStorage
        sessionStorage.setItem("mid", trimmedMid);

        if (showToast) {
            MessageToster("success", `Logged in successfully as Member #${trimmedMid}!`);
        }

        if (autoRedirect) {
            // Immediate replace ensures instant navigation to dashboard
            window.location.replace(redirectUrl);
        }

        return {
            success: true,
            token: tokenResult.token,
            mid: trimmedMid,
            message: "Login successful.",
            data: tokenResult.data,
        };
    } catch (e) {
        const msg = e.message || "Failed to set login session.";
        if (showToast) {
            MessageToster("error", msg);
        }
        return { success: false, message: msg, error: e };
    }
};
